export const adminCn = (...classes) => classes.filter(Boolean).join(" ");

export const adminControlClass =
  "h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100";

const buttonStyles = {
  primary:
    "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-300",
  secondary:
    "bg-slate-800 text-white hover:bg-slate-900 active:bg-black focus-visible:ring-slate-300",
  danger:
    "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-300",
  success:
    "bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 focus-visible:ring-emerald-300",
  ghost:
    "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 focus-visible:ring-slate-200",
};

export const AdminButton = ({ children, variant = "primary", className, ...props }) => (
  <button
    className={adminCn(
      "inline-flex h-11 cursor-pointer items-center justify-center rounded-xl px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-60",
      buttonStyles[variant] || buttonStyles.primary,
      className
    )}
    {...props}
  >
    {children}
  </button>
);

export const AdminField = ({ label, className, children }) => (
  <label className={adminCn("block min-w-0", className)}>
    {label ? (
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
    ) : null}
    {children}
  </label>
);

export const AdminInput = ({ className, ...props }) => (
  <input className={adminCn(adminControlClass, className)} {...props} />
);

export const AdminSelect = ({ className, children, ...props }) => (
  <select className={adminCn(adminControlClass, "cursor-pointer", className)} {...props}>
    {children}
  </select>
);

export const AdminTextarea = ({ className, ...props }) => (
  <textarea
    className={adminCn(
      "min-h-[110px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100",
      className
    )}
    {...props}
  />
);

export const AdminFormGrid = ({ className, children }) => (
  <div className={adminCn("grid grid-cols-1 gap-3 md:grid-cols-2", className)}>{children}</div>
);

export const AdminStatusBadge = ({ children, tone = "slate" }) => {
  const toneClass = {
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
    red: "bg-red-100 text-red-700 ring-red-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };

  return (
    <span
      className={adminCn(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
        toneClass[tone] || toneClass.slate
      )}
    >
      {children}
    </span>
  );
};

export const AdminScrollTable = ({ children }) => (
  <div className="overflow-hidden rounded-xl border border-slate-200">
    <div className="max-w-full overflow-x-auto overscroll-x-contain touch-pan-x [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-track]:bg-slate-100">
      {children}
    </div>
  </div>
);

export const AdminEmptyState = ({ text }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center text-sm text-slate-500">
    {text}
  </div>
);

export const AdminStatCard = ({ title, value, tone = "slate" }) => {
  const accent = {
    blue: "from-blue-50 to-blue-100/40 border-blue-200/70",
    green: "from-emerald-50 to-emerald-100/40 border-emerald-200/70",
    amber: "from-amber-50 to-amber-100/40 border-amber-200/70",
    slate: "from-slate-50 to-slate-100/40 border-slate-200/80",
  };

  return (
    <div className={adminCn("rounded-2xl border bg-gradient-to-br p-4", accent[tone] || accent.slate)}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <p className="mt-2 text-3xl font-black text-slate-900">{value}</p>
    </div>
  );
};

const AdminSectionCard = ({ title, subtitle, rightAction, children }) => (
  <section className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[0_12px_30px_-18px_rgba(15,23,42,0.35)] sm:p-6">
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div className="min-w-0">
        <h2 className="break-words text-lg font-extrabold tracking-tight text-slate-900 sm:text-xl">
          {title}
        </h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {rightAction ? <div className="shrink-0">{rightAction}</div> : null}
    </div>
    {children}
  </section>
);

export default AdminSectionCard;
